import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import AutomationAPI from '../../api/automation';

export const state = {
  records: [],
  accountId: null,
  generation: 0,
  requests: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
  },
};

export const getters = {
  getAutomations(_state) {
    return _state.records.sort((a1, a2) => a1.id - a2.id);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

// Each request belongs to an account generation. Returning to the same account
// does not make a response from a previous visit current again.
const beginRequest = (context, operation) => {
  const { commit, state: current, rootGetters } = context;
  const accountId = rootGetters?.getCurrentAccountId;
  commit('SYNC_ACCOUNT', accountId);
  commit('START_REQUEST', operation);
  const generation = current.generation;
  const requestId = current.requests[operation];
  return {
    current: () =>
      current.generation === generation &&
      rootGetters?.getCurrentAccountId === accountId,
    latest: () => current.requests[operation] === requestId,
  };
};

const mutate = async (context, operation, request, mutation) => {
  const guard = beginRequest(context, operation);
  const flag = `is${operation}`;
  context.commit(types.SET_AUTOMATION_UI_FLAG, { [flag]: true });
  try {
    const response = await request();
    if (!guard.current()) return false;
    if (mutation) mutation(response);
    return true;
  } catch (error) {
    if (guard.current()) throw error;
    return false;
  } finally {
    if (guard.current() && guard.latest()) {
      context.commit(types.SET_AUTOMATION_UI_FLAG, { [flag]: false });
    }
  }
};

export const actions = {
  syncAccount({ commit, rootGetters }) {
    commit('SYNC_ACCOUNT', rootGetters?.getCurrentAccountId);
  },
  async get(context) {
    const guard = beginRequest(context, 'Fetching');
    context.commit(types.SET_AUTOMATION_UI_FLAG, { isFetching: true });
    try {
      const response = await AutomationAPI.get();
      if (!guard.current() || !guard.latest()) return false;
      context.commit(types.SET_AUTOMATIONS, response.data.payload);
      return true;
    } catch (error) {
      if (guard.current() && guard.latest()) throw error;
      return false;
    } finally {
      if (guard.current() && guard.latest()) {
        context.commit(types.SET_AUTOMATION_UI_FLAG, { isFetching: false });
      }
    }
  },
  create: (context, payload) =>
    mutate(
      context,
      'Creating',
      () => AutomationAPI.create(payload),
      response => {
        context.commit(types.ADD_AUTOMATION, response.data);
      }
    ),
  update: (context, { id, ...payload }) =>
    mutate(
      context,
      'Updating',
      () => AutomationAPI.update(id, payload),
      response => {
        context.commit(types.EDIT_AUTOMATION, response.data.payload);
      }
    ),
  delete: (context, id) =>
    mutate(
      context,
      'Deleting',
      () => AutomationAPI.delete(id),
      () => {
        context.commit(types.DELETE_AUTOMATION, id);
      }
    ),
  clone: (context, id) =>
    mutate(context, 'Cloning', () => AutomationAPI.clone(id)),
  uploadAttachment: async (_, file) => {
    const { blobId } = await uploadFile(file);
    return blobId;
  },
};

export const mutations = {
  SYNC_ACCOUNT(current, accountId) {
    if (current.accountId === accountId) return;
    current.accountId = accountId;
    current.generation += 1;
    current.records = [];
    current.requests = {};
    Object.keys(current.uiFlags).forEach(key => {
      current.uiFlags[key] = false;
    });
  },
  START_REQUEST(current, operation) {
    current.requests[operation] = (current.requests[operation] || 0) + 1;
  },
  [types.SET_AUTOMATION_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.ADD_AUTOMATION]: MutationHelpers.create,
  [types.SET_AUTOMATIONS]: MutationHelpers.set,
  [types.EDIT_AUTOMATION]: MutationHelpers.update,
  [types.DELETE_AUTOMATION]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
